import { useEffect, useRef, useState } from "react";
import { connectDisplay, type ScreenConnection } from "./displayConnection.ts";
import {
  applyEvent,
  initialState,
  type DisplayEvent,
  type DisplayState,
} from "../shared/protocol.ts";

/**
 * Subscribes to the relay and folds incoming events into state.
 *
 * Same-origin by construction, so there is nothing to configure: in dev Vite
 * proxies /panel to the relay, in production Fastify serves this page itself.
 */

/**
 * Gemini uses parallel function calling: the tiles of one dashboard typically
 * arrive within milliseconds of each other. Without pacing, "build one by one"
 * collapses into a single pop. Live tiles therefore go through a reveal queue;
 * anything that replaces the screen wholesale (begin/clear/video) drops the
 * queue, and a `sync` (late joiner) always shows the full state at once.
 *
 * The interval is `config.revealMs` (v3), which is per-screen because the right
 * pacing depends on the room: a boardroom watching one dashboard wants the
 * build, a lobby panel people walk past wants it up.
 */

export function useDisplay(): { state: DisplayState; connected: boolean; connection: ScreenConnection } {
  const [state, setState] = useState<DisplayState>(initialState);
  const [connection, setConnection] = useState<ScreenConnection>("connecting");
  const seqRef = useRef(0);
  const queueRef = useRef<DisplayEvent[]>([]);
  const timerRef = useRef<number | undefined>(undefined);
  /**
   * Read by the drain loop, which lives outside React's render and must not
   * re-subscribe the socket when the pacing changes. A ref rather than a
   * dependency: putting `state.config.revealMs` in the effect's array would
   * tear down and rebuild the WebSocket every time the console saved a colour.
   */
  const revealMsRef = useRef(initialState.config.revealMs);
  revealMsRef.current = state.config.revealMs;

  useEffect(() => {
    const applyNow = (ev: DisplayEvent) =>
      setState((s) => applyEvent(s, ev));

    const dropQueue = () => {
      queueRef.current = [];
      window.clearTimeout(timerRef.current);
      timerRef.current = undefined;
    };

    const drain = () => {
      timerRef.current = undefined;
      const next = queueRef.current.shift();
      if (next) applyNow(next);
      if (queueRef.current.length) {
        timerRef.current = window.setTimeout(drain, revealMsRef.current);
      }
    };

    const enqueueTile = (ev: DisplayEvent) => {
      queueRef.current.push(ev);
      if (timerRef.current === undefined) drain();
    };

    const handle = (ev: DisplayEvent) => {
      switch (ev.type) {
        case "display.reload":
          // The console asked for this screen to refresh itself (§8.6). Do it
          // now; the fresh load re-syncs from the relay.
          window.location.reload();
          break;
        case "dashboard.tile":
          enqueueTile(ev);
          break;
        case "robot.status":
        case "screen.config":
          // Orthogonal to the screen content; must not disturb a reveal. A
          // config push mid-build re-themes the tiles already up and lets the
          // rest keep arriving.
          applyNow(ev);
          break;
        default:
          // begin / clear / video replace the screen: pending tiles belong to
          // the previous dashboard (or are superseded), so drop them.
          dropQueue();
          applyNow(ev);
      }
    };
    // A viewer credential is carried in the fragment, never sent in URL logs.
    const suppliedViewer = new URLSearchParams(location.hash.slice(1)).get('viewer');
    let viewerToken = suppliedViewer || '';
    try {
      if (suppliedViewer) sessionStorage.setItem('rafed-viewer', suppliedViewer);
      else viewerToken = sessionStorage.getItem('rafed-viewer') || '';
    } catch { /* Private browsing can disable storage; the fragment still works. */ }

    const proto = location.protocol === "https:" ? "wss" : "ws";
    const robot = new URLSearchParams(location.search).get("robot");
    const query = new URLSearchParams();
    if (robot) query.set('robot',robot);
    if (new URLSearchParams(location.search).get('preview') === '1') query.set('preview','1');
    const qs = query.size ? `?${query}` : '';
    const disconnect = connectDisplay({
      url: `${proto}://${location.host}/panel${qs}`,
      token: viewerToken,
      viewport: `${window.screen.width}x${window.screen.height}`,
      onStatus: setConnection,
      onMessage: msg => {
        if (msg.type === "sync") {
          seqRef.current = msg.seq;
          dropQueue();
          setState(msg.state);
          return true;
        }
        // A gap needs a fresh snapshot before applying any more events.
        if (msg.seq !== seqRef.current + 1) return false;
        seqRef.current = msg.seq;
        handle(msg);
        return true;
      },
    });
    return () => {
      disconnect();
      dropQueue();
    };
  }, []);

  return { state, connected: connection === "connected", connection };
}
